import React, { useState, useEffect } from 'react';
import { auth } from '../../firebase/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import Header from './Header';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const DataPrices = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleMenu = () => setIsOpen(!isOpen);
  const [user, setUser] = useState(null);

  const [dataPlans, setDataPlans] = useState({});
  const [loadingPlans, setLoadingPlans] = useState(true);
  
  const [selectedNetwork, setSelectedNetwork] = useState('MTN');
  const [selectedPlanType, setSelectedPlanType] = useState('');
  const [planSearch, setPlanSearch] = useState('');
  
  const [savingPrices, setSavingPrices] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const fetchDataPlans = async () => {
    if (!user) return;
    try {
      setLoadingPlans(true);
      const idToken = await user.getIdToken();
      const res = await fetch(`${apiUrl}/api/v1/data-plans`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDataPlans(data);

        // Default to MTN network with the AWOOF plan type. Keep the current
        // selection if it is still valid (e.g. after a manual refresh),
        // otherwise fall back to MTN + AWOOF (or the first available option).
        const networks = Object.keys(data);
        const defaultNetwork =
          selectedNetwork && data[selectedNetwork]
            ? selectedNetwork
            : (data['MTN'] ? 'MTN' : (networks[0] || ''));

        const types =
          defaultNetwork && data[defaultNetwork]
            ? Object.keys(data[defaultNetwork])
            : [];

        const defaultPlanType = types.includes(selectedPlanType)
          ? selectedPlanType
          : (types.find(t => t.toLowerCase().includes('awoof')) || types[0] || '');

        setSelectedNetwork(defaultNetwork);
        setSelectedPlanType(defaultPlanType);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingPlans(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchDataPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handlePriceUpdate = async (network, planType, planId, newPrices) => {
    setSavingPrices(true);
    try {
      const idToken = await user.getIdToken();
      const res = await fetch(`${apiUrl}/api/v1/admin/data-plans/${network}/${planType}/${planId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newPrices)
      });
      if (res.ok) {
        localStorage.removeItem('vtu_data_plans_cache');
        localStorage.removeItem('vtu_data_plans_cache_time');
        alert('Price updated successfully');
        await fetchDataPlans();
      } else {
        alert('Failed to update price');
      }
    } catch (e) {
      console.error(e);
      alert('Error updating price');
    } finally {
      setSavingPrices(false);
    }
  };

  const getFilteredPlans = () => {
    if (!dataPlans[selectedNetwork] || !dataPlans[selectedNetwork][selectedPlanType]) return [];
    let plans = dataPlans[selectedNetwork][selectedPlanType];
    
    if (planSearch) {
        plans = plans.filter(p => p.plan_id && p.plan_id.toString().includes(planSearch.trim()));
    }
    return plans;
  };

  const activeNetworks = Object.keys(dataPlans);
  const activePlanTypes = dataPlans[selectedNetwork] ? Object.keys(dataPlans[selectedNetwork]) : [];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-300">
      <Header onToggleMenuClick={toggleMenu} isOpen={isOpen} />
      <div className={`pt-16 px-4 sm:px-6 lg:px-8 transition-all duration-300 ${isOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        <main className="max-w-6xl mx-auto py-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
              Data Prices Management
            </h2>
            <button onClick={fetchDataPlans} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">
                Refresh Plans
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1">
                    <label className="block text-sm font-medium mb-1">Network</label>
                    <select 
                        value={selectedNetwork} 
                        onChange={(e) => {
                            const net = e.target.value;
                            setSelectedNetwork(net);
                            if (dataPlans[net]) {
                                const types = Object.keys(dataPlans[net]);
                                const awoofType = types.find(t => t.toLowerCase().includes('awoof'));
                                setSelectedPlanType(awoofType || types[0] || '');
                            }
                        }}
                        className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
                    >
                        {activeNetworks.map(net => <option key={net} value={net}>{net.toUpperCase()}</option>)}
                    </select>
                </div>
                <div className="flex-1">
                    <label className="block text-sm font-medium mb-1">Plan Type</label>
                    <select 
                        value={selectedPlanType} 
                        onChange={(e) => setSelectedPlanType(e.target.value)}
                        className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
                    >
                        {activePlanTypes.map(type => <option key={type} value={type}>{type.toUpperCase()}</option>)}
                    </select>
                </div>
                <div className="flex-1">
                    <label className="block text-sm font-medium mb-1">Search Plan ID</label>
                    <input 
                        type="text" 
                        placeholder="E.g. 10" 
                        value={planSearch} 
                        onChange={(e) => setPlanSearch(e.target.value)}
                        className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
                    />
                </div>
            </div>

            {loadingPlans ? (
                <p className="text-center py-4">Loading plans...</p>
            ) : (
                <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Validity</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price (₦)</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Upgraded (₦)</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">API (₦)</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {getFilteredPlans().map(plan => (
                      <DataPlanRow 
                        key={plan.plan_id} 
                        plan={plan} 
                        onSave={(newPrices) => handlePriceUpdate(selectedNetwork, selectedPlanType, plan.plan_id, newPrices)}
                        saving={savingPrices}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

const DataPlanRow = ({ plan, onSave, saving }) => {
  const [price, setPrice] = useState(plan.price || 0);
  const [upgradedPrice, setUpgradedPrice] = useState(plan.upgraded_user_price || 0);
  const [apiPrice, setApiPrice] = useState(plan.api_user_price || 0);

  // Sync state if props change (e.g., after save/refresh)
  useEffect(() => {
    setPrice(plan.price || 0);
    setUpgradedPrice(plan.upgraded_user_price || 0);
    setApiPrice(plan.api_user_price || 0);
  }, [plan]);

  const hasChanges = 
    Number(price) !== Number(plan.price || 0) || 
    Number(upgradedPrice) !== Number(plan.upgraded_user_price || 0) || 
    Number(apiPrice) !== Number(plan.api_user_price || 0);

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
      <td className="px-4 py-3 text-sm font-medium">{plan.plan_id}</td>
      <td className="px-4 py-3 text-sm">{plan.name || plan.plan}</td>
      <td className="px-4 py-3 text-sm">{plan.validity || plan.month_validate || 'N/A'}</td>
      <td className="px-4 py-3 text-sm font-semibold text-gray-500">₦{plan.cost || plan.api_price || 0}</td>
      <td className="px-4 py-3 text-sm">
        <input 
          type="number" 
          value={price} 
          onChange={(e) => setPrice(Number(e.target.value))}
          className="w-20 px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600"
        />
      </td>
      <td className="px-4 py-3 text-sm">
        <input 
          type="number" 
          value={upgradedPrice} 
          onChange={(e) => setUpgradedPrice(Number(e.target.value))}
          className="w-20 px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600"
        />
      </td>
      <td className="px-4 py-3 text-sm">
        <input 
          type="number" 
          value={apiPrice} 
          onChange={(e) => setApiPrice(Number(e.target.value))}
          className="w-20 px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600"
        />
      </td>
      <td className="px-4 py-3 text-sm">
        <button 
          onClick={() => onSave({ price, upgraded_user_price: upgradedPrice, api_user_price: apiPrice })}
          disabled={!hasChanges || saving}
          className={`px-3 py-1 rounded text-white text-xs font-medium ${!hasChanges || saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
        >
          {saving && hasChanges ? 'Saving...' : 'Save'}
        </button>
      </td>
    </tr>
  );
};

export default DataPrices;
